import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { getAutomationStack } from '@confluence/automation-discovery/entry-points/AutomationAPIUtilities.ts';
import { AUTOMATION_SMART_BUTTON_EDIT_RULE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type { experimentalGetAutomationRuleByIdQuery_experimentalGetAutomationRuleById as RuleDetails } from '../__types__/GetAutomationRuleByIdQuery.ts';
import type { TemplateFormData } from '../smart-button-form-data/smartButtonFormReducer.ts';
import { useExperimentalGetAutomationRuleByIdQuery } from '../queries/useExperimentalGetAutomationRuleByIdQuery.ts';

type EditRuleContext = {
	isLoading: boolean;
	isTemplateEditor: boolean;
	canEditRule: boolean;
	ruleDetails?: RuleDetails;
	editSmartRuleTemplateId?: string | null;
	editSmartRuleConfig?: Partial<TemplateFormData>;
	error?: ApolloErrorInterop;
};

type EditSmartButtonRuleProviderProps = {
	ruleId?: string;
	isTemplateEditor: boolean;
	children?: React.ReactNode;
};

const getTemplateFormDataFromRuleConfigByTemplateId = (
	ruleConfig?: RuleDetails | null,
	templateId?: string | null,
): TemplateFormData => {
	if (!ruleConfig?.components || !templateId) {
		return {};
	}
	const { components } = ruleConfig;

	const formData: TemplateFormData = {};

	components.forEach((component) => {
		switch (component?.type) {
			case 'confluence.change-page-status.action':
				formData.changePageStatusActionConfig = { ...component.value };
				break;
			case 'outgoing.email':
				// component.value has more fields than the config wants, but doesn't seem to be an issue?
				formData.outgoingEmailActionConfig = { ...component.value };
				break;
			case 'confluence.add-label.action':
				formData.addLabelActionConfig = { ...component.value };
				break;
			case 'confluence.add-comment.action':
				formData.addCommentActionConfig = { ...component.value };
				break;
			case 'confluence.create-page.action':
				formData.createPageActionConfig = { ...component.value };
				break;
			case 'confluence.restrict-page.action':
				formData.restrictPageActionConfig = { ...component.value };
				break;
			case 'confluence.change-page-owner.action':
				formData.changePageOwnerActionConfig = { ...component.value };
				break;
			case 'confluence.copy-page.action':
				formData.copyPageActionConfig = { ...component.value };
				break;
			case 'confluence.move-page.action':
				formData.movePageActionConfig = { ...component.value };
				break;
			case 'confluence.update-data-classification.action':
				formData.updateDataClassificationActionConfig = { ...component.value };
				break;
			case 'confluence.generate-ai-summary.action':
				formData.generateAISummaryActionConfig = { ...component.value };
				break;
			case 'confluence.generate-ai-action-items.action':
				formData.generateAIActionItemsActionConfig = { ...component.value };
				break;
			case 'confluence.create-database.action':
				formData.createDatabaseActionConfig = { ...component.value };
				break;
			case 'jira.issue.create':
				delete component.value['advancedFields'];
				delete component.value['sendNotifications'];
				formData.createJiraIssueActionConfig = { ...component.value };
				break;
			case 'confluence.create-whiteboard.action':
				formData.createWhiteboardActionConfig = { ...component.value };
				break;
			case 'confluence.create-folder.action':
				formData.createFolderActionConfig = { ...component.value };
				break;
			case 'rovo.invoke-agent.action':
				formData.invokeAgentActionConfig = { ...component.value };
				break;
		}
	});
	return formData;
};

const removeTypenameFields = (value: any): any => {
	if (value === null || value === undefined) {
		return value;
	} else if (Array.isArray(value)) {
		return value.map((v) => removeTypenameFields(v));
	} else if (typeof value === 'object') {
		const newObj: any = {};
		Object.entries(value).forEach(([key, v]) => {
			if (key !== '__typename') {
				newObj[key] = removeTypenameFields(v);
			}
		});
		return newObj;
	}
	return value;
};

const getCanEditRule = ({
	isRuleOwner,
	ruleId,
	isTemplateEditor,
}: {
	isRuleOwner: boolean;
	ruleId?: string;
	isTemplateEditor: boolean;
}): boolean => {
	if (isTemplateEditor) {
		return false;
	}

	return !ruleId || isRuleOwner;
};

const EditRuleContext = createContext<EditRuleContext>({
	isLoading: false,
	isTemplateEditor: false,
	canEditRule: false,
	ruleDetails: undefined,
	editSmartRuleTemplateId: null,
	editSmartRuleConfig: {},
	error: undefined,
});

export const EditSmartButtonRuleProvider: React.FC<EditSmartButtonRuleProviderProps> = ({
	ruleId,
	isTemplateEditor,
	children,
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { cloudId, environment, userId } = useSessionData();
	const spaceKey = useSpaceKey();
	const spaceId = useSpaceId(spaceKey ?? '');
	const [automationStack, setAutomationStack] = useState<string>();

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		getAutomationStack(environment).then((res) => setAutomationStack(res));
	}, [environment]);

	const { loading, data, error } = useExperimentalGetAutomationRuleByIdQuery({
		skip: !ruleId || !automationStack || !spaceId,
		fetchPolicy: 'cache-and-network' as const,
		variables: {
			ruleId: ruleId ?? '',
			spaceId: spaceId ?? '',
			cloudId,
			automationStack: automationStack ?? '',
		},
	});

	if (error) {
		experienceTracker.fail({
			name: AUTOMATION_SMART_BUTTON_EDIT_RULE,
			error,
		});
		markErrorAsHandled(error);
	}

	const ruleDetails = data?.experimentalGetAutomationRuleById;

	const templateId = ruleDetails?.tags?.find((tag) => tag?.tagType === 'TEMPLATE_ID')?.tagValue;

	const templateFormData = getTemplateFormDataFromRuleConfigByTemplateId(ruleDetails, templateId);

	const isRuleOwner = ruleDetails?.authorAccountId === userId;

	const canEditRule = getCanEditRule({
		isRuleOwner,
		ruleId,
		isTemplateEditor,
	});

	const contextValue = useMemo(
		() => ({
			isLoading: loading,
			isTemplateEditor,
			canEditRule,
			editSmartRuleTemplateId: templateId,
			editSmartRuleConfig: templateFormData,
			// TODO: should be improved with official GQL mutation that doesn't require entire rule to make updates
			ruleDetails: removeTypenameFields({ ...ruleDetails }),
			error,
		}),
		[templateFormData, loading, templateId, error, ruleDetails, canEditRule, isTemplateEditor],
	);

	return <EditRuleContext.Provider value={contextValue}>{children}</EditRuleContext.Provider>;
};

export const useEditRuleData = () => {
	const context = useContext(EditRuleContext);
	if (context === undefined) {
		throw new Error('useEditRuleData must be used within a EditSmartButtonRuleProvider');
	}
	return context;
};
